import React, { useContext, useMemo } from 'react';
import styled, { css } from 'styled-components';
import moment from 'moment';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import { ReactComponent as HasLinkIcon } from '@ubeya/shared-web/assets/has-link.svg';
import { ReactComponent as HasNoLinkIcon } from '@ubeya/shared-web/assets/has-no-link.svg';
import { Tooltip } from '@ubeya/shared-web/components';

const Link = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  text-decoration: none;
  white-space: nowrap;
  overflow: hidden;
  min-width: 0;
  max-width: 100%;
  color: inherit;
  padding-block: 4px;
  padding-inline: 6px 8px;
  border-radius: 90px;
  background: ${({ theme }) => theme.colors.grey1};

  ${({ $clickable, theme }) =>
    $clickable &&
    css`
      &:hover {
        background: ${theme.colors.lightHoverBackground};
        text-decoration: underline;
      }
    `}

  svg {
    width: 20px;
    height: 20px;
    flex-shrink: 0;

    path {
      fill: ${({ theme }) => theme.colors.grey4};
    }
  }
`;

const LinkText = styled.div`
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0px;
  white-space: nowrap;
  line-height: 20px;
  font-size: 12px;
  font-weight: 400;
`;

const EventLink = ({ ticket, accountId, compact = false, showEmpty = false, disableLink = false }) => {
  const { t, dateFormat } = useContext(ConfigContext);
  const shiftDetails = ticket.shiftDetails;

  const breadcrumb = useMemo(() => {
    if (!shiftDetails) return null;
    const parts = [];
    if (shiftDetails.projectName) parts.push(shiftDetails.projectName);
    if (shiftDetails.positionName) parts.push(shiftDetails.positionName);
    if (shiftDetails.date) {
      parts.push(moment(shiftDetails.date).format(dateFormat || 'MMM DD, YYYY'));
    }
    if (shiftDetails.startTime && shiftDetails.endTime) {
      const start = moment(shiftDetails.startTime).format('HH:mm');
      const end = moment(shiftDetails.endTime).format('HH:mm');
      parts.push(`${start}-${end}`);
    }
    return parts.length > 0 ? parts.join(' \u00B7 ') : null;
  }, [shiftDetails, dateFormat]);

  const hasLink = !!(ticket.shiftId && shiftDetails) || !!ticket.eventId;
  const eventText = breadcrumb || ticket.eventName || '';

  const href =
    ticket.shiftId && shiftDetails?.projectId
      ? `/${accountId}/scheduling/branch/${ticket.branchId}/project/${shiftDetails.projectId}`
      : ticket.eventId
      ? `/${accountId}/orders/${ticket.eventId}`
      : null;

  if (!hasLink && !eventText) {
    if (!showEmpty) return null;

    return (
      <Link as="span">
        <HasNoLinkIcon />
        {!compact && <LinkText>{t('help_desk_no_event')}</LinkText>}
      </Link>
    );
  }

  if (!href || disableLink) {
    return (
      <Tooltip body={eventText || ''}>
        <Link as="span" title={eventText}>
          <HasLinkIcon />
          {!compact && <LinkText>{eventText}</LinkText>}
        </Link>
      </Tooltip>
    );
  }

  return (
    <Tooltip body={eventText || t('help_desk_view_shift')}>
      <Link href={href} target="_blank" rel="noopener noreferrer" $clickable>
        <HasLinkIcon />
        {!compact && <LinkText>{eventText || t('help_desk_view_shift')}</LinkText>}
      </Link>
    </Tooltip>
  );
};

export default EventLink;
